import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { ArrowLeft, X, ImagePlus, Loader2, Camera, Phone, CalendarDays, MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';
import useShopTools from '../../hooks/useShopTools';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Geocode "indirizzo + città" via OpenStreetMap when GPS coords are missing.
 *  Tries structured (street+city) first; falls back to free-text, then to
 *  city only — so an event ALWAYS gets a position as long as the city exists.
 */
async function geocodeAddress(indirizzo, citta) {
  const streetTrim = (indirizzo || '').trim();
  const cityTrim = (citta || '').trim();
  if (!streetTrim && !cityTrim) return null;
  const tryFetch = async (url) => {
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch (e) {
      console.warn('Geocoding fetch failed:', e);
    }
    return null;
  };
  if (streetTrim && cityTrim) {
    const c = await tryFetch(`https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(streetTrim)}&city=${encodeURIComponent(cityTrim)}&country=Italia&limit=1`);
    if (c) return c;
  }
  const q = encodeURIComponent(`${streetTrim}, ${cityTrim}, Italia`.replace(/^,\s*/, ''));
  const c2 = await tryFetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`);
  if (c2) return c2;
  if (cityTrim) {
    return await tryFetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(cityTrim + ', Italia')}`);
  }
  return null;
}

const TIPO_LABEL = {
  product: { sing: 'prodotto', label: 'Prodotto' },
  service: { sing: 'servizio', label: 'Servizio' },
  event:   { sing: 'evento',   label: 'Evento' },
};

export default function ProductForm({ product, onSave, onCancel, tipo: tipoProp }) {
  const { isEnabled: toolEnabled } = useShopTools();
  const showInventario = toolEnabled('inventario');
  const showBarcode = toolEnabled('barcode');
  const tipo = product?.tipo || tipoProp || 'product';
  const isEvent = tipo === 'event';
  const isService = tipo === 'service';
  const { sing: itemSing, label: itemLabel } = TIPO_LABEL[tipo] || TIPO_LABEL.product;

  const [formData, setFormData] = useState({
    nome: product?.nome || '',
    descrizione: product?.descrizione || '',
    disponibilita: product?.disponibilita || 'disponibile',
    prezzo: product?.prezzo || '',
    sconto: product?.sconto || '',
    immagini: product?.immagini || [],
    tag: product?.tag?.join(', ') || '',
    // service-only — centralized phone toggle (v1.7.0). The phone number itself
    // is read from the shop's "Dati Negozio" entry.
    mostra_telefono: product?.mostra_telefono ?? Boolean(product?.telefono_prenotazione),
    // event-only
    data_inizio: product?.data_inizio || '',
    data_fine: product?.data_fine || '',
    orario_inizio: product?.orario_inizio || '',
    orario_fine: product?.orario_fine || '',
    evento_indirizzo: product?.evento_indirizzo || '',
    evento_citta: product?.evento_citta || '',
    evento_lat: product?.evento_lat || null,
    evento_lng: product?.evento_lng || null,
    // product-only (inventory)
    barcode: product?.barcode || '',
    sku: product?.sku || '',
    categoria: product?.categoria || '',
    quantita_magazzino: product?.quantita_magazzino ?? 0,
    // v1.8.5 — publication toggle, uniform for products/services/events.
    // New elements default to NOT public (`pubblicato=false`) so the owner
    // opts in explicitly.
    pubblicato: product?.pubblicato ?? false,
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    setError(null);

    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        uploadedUrls.push(imageUrl);
      }

      setFormData(prev => ({
        ...prev,
        immagini: [...prev.immagini, ...uploadedUrls],
      }));
    } catch (err) {
      console.error('Upload error:', err);
      setError('Errore durante il caricamento delle immagini');
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      immagini: prev.immagini.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.nome || !formData.descrizione) {
      setError('Nome e descrizione sono obbligatori');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        nome: formData.nome,
        descrizione: formData.descrizione,
        disponibilita: formData.disponibilita,
        prezzo: formData.prezzo ? parseFloat(formData.prezzo) : null,
        sconto: formData.sconto ? parseInt(formData.sconto, 10) : null,
        immagini: formData.immagini,
        tag: formData.tag ? formData.tag.split(',').map((t) => t.trim()).filter(Boolean) : [],
        tipo,
        // v1.8.5 — publication flag is the ONLY visibility source of truth.
        pubblicato: !!formData.pubblicato,
      };
      if (isService) {
        payload.mostra_telefono = !!formData.mostra_telefono;
      }
      if (tipo === 'product') {
        payload.barcode = formData.barcode?.trim() || '';
        payload.sku = formData.sku?.trim() || '';
        payload.categoria = formData.categoria?.trim() || '';
        payload.quantita_magazzino = parseInt(formData.quantita_magazzino, 10) || 0;
      }
      if (isEvent) {
        payload.data_inizio = formData.data_inizio || null;
        payload.data_fine = formData.data_fine || formData.data_inizio || null;
        payload.orario_inizio = formData.orario_inizio || null;
        payload.orario_fine = formData.orario_fine || null;
        payload.evento_indirizzo = formData.evento_indirizzo || '';
        payload.evento_citta = formData.evento_citta || '';
        payload.evento_lat = formData.evento_lat || null;
        payload.evento_lng = formData.evento_lng || null;
        // Fallback: if user didn't pin the map, geocode from city + street.
        if ((!payload.evento_lat || !payload.evento_lng) && (formData.evento_citta || formData.evento_indirizzo)) {
          const coords = await geocodeAddress(formData.evento_indirizzo, formData.evento_citta);
          if (coords) {
            payload.evento_lat = coords.lat;
            payload.evento_lng = coords.lng;
          }
        }
      }

      let response;
      if (product) {
        response = await axios.put(`${API}/products/${product.id}`, payload, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/products`, payload, { withCredentials: true });
      }

      onSave(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="product-form">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onCancel}
          className="p-2 -ml-2 hover:bg-slate-100 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <h2 className="font-outfit font-bold text-xl text-slate-800">
          {product ? `Modifica ${itemSing}` : `Nuovo ${itemSing}`}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Images */}
        <div>
          <Label className="text-sm font-medium text-slate-700">Immagini</Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {formData.immagini.map((url, index) => (
              <div
                key={url || `img-${index}`}
                className="relative w-24 h-24 rounded-xl overflow-hidden bg-slate-100"
              >
                <img
                  src={url}
                  alt={`Immagine ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                  data-testid={`remove-image-${index}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {/* Camera button (mobile) */}
            <label className="w-24 h-24 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-100 transition-colors">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="camera-input"
              />
              {uploadingImages ? (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              ) : (
                <>
                  <Camera className="w-6 h-6 text-blue-500" />
                  <span className="text-xs text-blue-600 mt-1 font-medium">Scatta</span>
                </>
              )}
            </label>

            {/* Gallery upload button */}
            <label className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="image-upload-input"
              />
              {uploadingImages ? (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              ) : (
                <>
                  <ImagePlus className="w-6 h-6 text-slate-400" />
                  <span className="text-xs text-slate-500 mt-1">Galleria</span>
                </>
              )}
            </label>
          </div>
        </div>

        {/* Name */}
        <div>
          <Label htmlFor="nome" className="text-sm font-medium text-slate-700">Nome {itemSing} *</Label>
          <Input
            id="nome"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            placeholder="es. Nike Air Max 270"
            className="mt-1 rounded-xl"
            data-testid="product-name-input"
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="descrizione" className="text-sm font-medium text-slate-700">Descrizione *</Label>
          <Textarea
            id="descrizione"
            name="descrizione"
            value={formData.descrizione}
            onChange={handleChange}
            placeholder={`Descrivi il ${itemSing}...`}
            rows={3}
            className="mt-1 rounded-xl"
            data-testid="product-description-input"
          />
        </div>

        {/* Price and Discount */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="prezzo" className="text-sm font-medium text-slate-700">Prezzo (€)</Label>
            <Input
              id="prezzo"
              name="prezzo"
              type="number"
              step="0.01"
              value={formData.prezzo}
              onChange={handleChange}
              placeholder="99.00"
              className="mt-1 rounded-xl"
              data-testid="product-price-input"
            />
          </div>
          <div>
            <Label htmlFor="sconto" className="text-sm font-medium text-slate-700">Sconto (%)</Label>
            <Input
              id="sconto"
              name="sconto"
              type="number"
              min="0"
              max="100"
              value={formData.sconto}
              onChange={handleChange}
              placeholder="20"
              className="mt-1 rounded-xl"
              data-testid="product-discount-input"
            />
          </div>
        </div>

        {/* Tags */}
        <div>
          <Label htmlFor="tag" className="text-sm font-medium text-slate-700">
            Hashtag (separati da virgola)
          </Label>
          <Input
            id="tag"
            name="tag"
            value={formData.tag}
            onChange={handleChange}
            placeholder="sneaker, nike, sportivo"
            className="mt-1 rounded-xl"
            data-testid="product-tags-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Gli hashtag aiutano gli utenti a trovare il tuo {itemSing}
          </p>
        </div>

        {/* PRODUCT: inventory-related fields — visible only when the
            corresponding tool is enabled (v1.8.0). Categoria è sempre
            visibile come classificazione base; Barcode + SKU sono
            insieme come "codici prodotto" quando Sistema Barcode è ON;
            Quantità richiede Inventario ON. */}
        {tipo === 'product' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3" data-testid="product-inventory-extras">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showBarcode && (
                <div>
                  <Label htmlFor="barcode" className="text-sm font-medium text-slate-700">Barcode</Label>
                  <Input id="barcode" name="barcode" value={formData.barcode} onChange={handleChange}
                    placeholder="es. 8012345678901" className="mt-1 rounded-xl bg-white" data-testid="product-barcode-input" />
                </div>
              )}
              {showBarcode && (
                <div>
                  <Label htmlFor="sku" className="text-sm font-medium text-slate-700">SKU / codice articolo</Label>
                  <Input id="sku" name="sku" value={formData.sku} onChange={handleChange}
                    placeholder="es. AQ-150" className="mt-1 rounded-xl bg-white" data-testid="product-sku-input" />
                </div>
              )}
              <div>
                <Label htmlFor="categoria" className="text-sm font-medium text-slate-700">Categoria</Label>
                <Input id="categoria" name="categoria" value={formData.categoria} onChange={handleChange}
                  placeholder="es. Bevande" className="mt-1 rounded-xl bg-white" data-testid="product-categoria-input" />
              </div>
              {showInventario && (
                <div>
                  <Label htmlFor="quantita_magazzino" className="text-sm font-medium text-slate-700">Quantità a magazzino</Label>
                  <Input id="quantita_magazzino" name="quantita_magazzino" type="number" min={0}
                    value={formData.quantita_magazzino} onChange={handleChange}
                    className="mt-1 rounded-xl bg-white" data-testid="product-qty-input" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* SERVICE: show centralized shop phone toggle */}
        {isService && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3" data-testid="service-extras">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label htmlFor="mostra_telefono" className="flex items-center gap-2 text-sm font-semibold text-emerald-800 cursor-pointer">
                  <Phone className="w-4 h-4" /> Mostra numero di telefono
                </Label>
                <p className="text-xs text-emerald-700 mt-1">
                  Se attivo, i clienti vedranno un pulsante <strong>&quot;Chiama per prenotare&quot;</strong> con il numero configurato nei <em>Dati negozio</em>. Modificalo lì per aggiornarlo ovunque.
                </p>
              </div>
              <Switch
                id="mostra_telefono"
                checked={!!formData.mostra_telefono}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, mostra_telefono: !!checked }))
                }
                data-testid="service-show-phone-toggle"
              />
            </div>
          </div>
        )}

        {/* EVENT: dates, place, poster */}
        {isEvent && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl space-y-3" data-testid="event-extras">
            <div className="flex items-center gap-2 text-sm font-semibold text-orange-800">
              <CalendarDays className="w-4 h-4" /> Dettagli evento
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">Dal giorno</Label>
                <Input type="date" name="data_inizio" value={formData.data_inizio} onChange={handleChange}
                  className="mt-1 rounded-xl bg-white" data-testid="event-start-date" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Al giorno (vuoto = stesso giorno)</Label>
                <Input type="date" name="data_fine" value={formData.data_fine} onChange={handleChange}
                  className="mt-1 rounded-xl bg-white" data-testid="event-end-date" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Dalle ore</Label>
                <Input type="time" name="orario_inizio" value={formData.orario_inizio} onChange={handleChange}
                  className="mt-1 rounded-xl bg-white" data-testid="event-start-time" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Alle ore</Label>
                <Input type="time" name="orario_fine" value={formData.orario_fine} onChange={handleChange}
                  className="mt-1 rounded-xl bg-white" data-testid="event-end-time" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Città
              </Label>
              <Input name="evento_citta" value={formData.evento_citta} onChange={handleChange}
                placeholder="es. Conselve" className="mt-1 rounded-xl bg-white" data-testid="event-city" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Indirizzo</Label>
              <Input name="evento_indirizzo" value={formData.evento_indirizzo} onChange={handleChange}
                placeholder="es. Piazza Mercato" className="mt-1 rounded-xl bg-white" data-testid="event-address" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Posizione sulla mappa (opzionale)</Label>
              <LocationPicker lat={formData.evento_lat} lng={formData.evento_lng}
                onChange={(lat, lng) => setFormData((prev) => ({ ...prev, evento_lat: lat, evento_lng: lng }))} />
            </div>
          </div>
        )}

        {/* Availability - toggle "Esaurito" (hide for events) */}
        {!isEvent && (
        <div
          className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
            formData.disponibilita === 'esaurito'
              ? 'bg-blue-50 border-blue-500'
              : 'bg-slate-50 border-slate-200'
          }`}
          data-testid="availability-section"
        >
          <div className="flex flex-col">
            <Label className={`text-sm font-bold ${
              formData.disponibilita === 'esaurito' ? 'text-blue-700' : 'text-slate-700'
            }`}>
              {itemLabel} esaurito
            </Label>
            <span className="text-xs text-slate-500 mt-0.5">
              {formData.disponibilita === 'esaurito'
                ? 'I clienti vedranno il badge "ESAURITO" sulla card'
                : `Attiva per segnalare il ${itemSing} come esaurito`}
            </span>
          </div>
          <Switch
            checked={formData.disponibilita === 'esaurito'}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({
                ...prev,
                disponibilita: checked ? 'esaurito' : 'disponibile',
              }))
            }
            className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300 scale-125"
            data-testid="product-availability-switch"
          />
        </div>
        )}

        {error && (
          <p className="text-red-500 text-sm bg-red-50 p-3 rounded-xl" data-testid="product-error">
            {error}
          </p>
        )}

        {/* v1.8.5 — Publish toggle: uniform for products, services, events.
            Only visibility source of truth for end users. */}
        <div
          className="p-4 bg-slate-50 rounded-xl flex items-center justify-between gap-4"
          data-testid="product-publish-block"
        >
          <div className="flex flex-col">
            <Label className={`text-sm font-bold ${
              formData.pubblicato ? 'text-emerald-700' : 'text-slate-700'
            }`}>
              Pubblico
            </Label>
            <span className="text-xs text-slate-500 mt-0.5">
              {formData.pubblicato
                ? `Questo ${itemSing} è visibile agli utenti su Kyloo`
                : `Questo ${itemSing} è privato: solo tu puoi vederlo`}
            </span>
          </div>
          <Switch
            checked={!!formData.pubblicato}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, pubblicato: !!checked }))
            }
            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300 scale-125"
            data-testid="product-publish-switch"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 rounded-xl"
            data-testid="cancel-button"
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={loading || uploadingImages}
            className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-xl"
            data-testid="save-product-button"
          >
            {loading ? 'Salvataggio...' : product ? 'Salva modifiche' : `Aggiungi ${itemSing}`}
          </Button>
        </div>
      </form>
    </div>
  );
}
